import type { SeatOrderResponse } from "@/types/api";

/**
 * The most seats one order can buy. Mirrors `SeatOrder.MaxSeatsPerOrder` on the API, which enforces it; this only
 * lets the dialog say so before the purchaser continues.
 */
export const MAX_SEATS_PER_ORDER = 500;

/** "1 seat" or "5 seats". */
export const formatSeatCount = (count: number) => (count === 1 ? "1 seat" : `${count} seats`);

/**
 * Reads the number of seats typed into the dialog: a whole number from 1 to `max` ({@link MAX_SEATS_PER_ORDER} unless
 * fewer are available, e.g. a session's spaces left), otherwise null.
 */
export function parseSeatCount(value: string, max: number = MAX_SEATS_PER_ORDER): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const count = Number(trimmed);
  return count >= 1 && count <= Math.min(max, MAX_SEATS_PER_ORDER) ? count : null;
}

/**
 * What happens to seats on an instructor-led session that nobody takes up (LMS-190, decided 2026-10-27). Checkout and
 * the order page both say so; Stripe's page says the same.
 */
export const SESSION_SEATS_LAPSE_MESSAGE =
  "Seats that haven't been taken up by the time the session starts lapse, and aren't refunded.";

/** The shape of an address the API accepts (mirrors the API's `Email` check; the API has the final say). */
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export interface ParsedEmailList {
  /** Valid addresses in the order entered, each once (case and spaces ignored). */
  emails: string[];
  /** Entries that aren't email addresses, as entered. */
  invalid: string[];
}

/**
 * Reads the email addresses typed or pasted into the invite box (LMS-145): separated by commas, semicolons, spaces or
 * new lines, and also as pasted from an email client (`Ann Smith <ann@example.com>`). Addresses are compared without
 * case, so one entered twice counts once.
 */
export function parseEmailList(text: string): ParsedEmailList {
  const emails: string[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();

  for (const part of text.split(/[,;\n\r]+/)) {
    // `Ann Smith <ann@example.com>`: the address is in the brackets and the words around it are a name. Anything
    // else in the segment that looks like an address (has an @) is read too, so nothing typed is silently dropped.
    const bracketed = [...part.matchAll(/<([^<>]*)>/g)].map((m) => m[1]);
    const outside = part.replace(/<[^<>]*>/g, " ").split(/\s+/);
    const candidates =
      bracketed.length > 0
        ? [...bracketed, ...outside.filter((word) => word.includes("@"))]
        : outside;
    for (const candidate of candidates) {
      const value = candidate.trim();
      if (!value) continue;
      if (!EMAIL_PATTERN.test(value)) {
        invalid.push(value);
        continue;
      }
      const key = value.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      emails.push(value);
    }
  }

  return { emails, invalid };
}

/** How a purchaser pays for seats (LMS-151): by card through Stripe, or by an invoice ARC raises. */
export type SeatPaymentChoice = "card" | "invoice";

/** What the purchaser enters to pay by invoice (LMS-151). The PO number and VAT number are optional. */
export interface InvoiceBillingValues {
  billingName: string;
  billingEmail: string;
  billingAddress: string;
  poNumber: string;
  vatNumber: string;
}

export const EMPTY_INVOICE_BILLING: InvoiceBillingValues = {
  billingName: "",
  billingEmail: "",
  billingAddress: "",
  poNumber: "",
  vatNumber: "",
};

/** The longest each billing field can be. Mirrors `InvoiceBillingDetails` on the API, which enforces it. */
export const INVOICE_BILLING_MAX_LENGTHS: Record<keyof InvoiceBillingValues, number> = {
  billingName: 200,
  billingEmail: 320,
  billingAddress: 1000,
  poNumber: 100,
  vatNumber: 50,
};

/** Why each billing field can't be sent as it is, if it can't (the API has the final say). */
export function invoiceBillingErrors(
  values: InvoiceBillingValues,
): Partial<Record<keyof InvoiceBillingValues, string>> {
  const errors: Partial<Record<keyof InvoiceBillingValues, string>> = {};
  if (!values.billingName.trim()) errors.billingName = "Enter the billing name or organisation.";
  if (!values.billingEmail.trim()) errors.billingEmail = "Enter the billing email.";
  else if (!EMAIL_PATTERN.test(values.billingEmail.trim()))
    errors.billingEmail = "Enter a valid email address.";
  if (!values.billingAddress.trim()) errors.billingAddress = "Enter the billing address.";
  for (const key of Object.keys(INVOICE_BILLING_MAX_LENGTHS) as (keyof InvoiceBillingValues)[]) {
    if (!errors[key] && values[key].trim().length > INVOICE_BILLING_MAX_LENGTHS[key])
      errors[key] = `Use ${INVOICE_BILLING_MAX_LENGTHS[key]} characters or fewer.`;
  }
  return errors;
}

/**
 * Why the PO number given for a card order (LMS-193) can't be sent as it is, if it can't: the only field a card order
 * takes (the API has the final say).
 */
export function cardPaymentErrors(
  values: InvoiceBillingValues,
): Partial<Record<keyof InvoiceBillingValues, string>> {
  return values.poNumber.trim().length > INVOICE_BILLING_MAX_LENGTHS.poNumber
    ? { poNumber: `Use ${INVOICE_BILLING_MAX_LENGTHS.poNumber} characters or fewer.` }
    : {};
}

/**
 * A card order's PO number as the API takes it (LMS-193): `{ poNumber }` trimmed, or nothing when none was given, so
 * the request is the same as one without the field.
 */
export const toCardPoNumber = (values: InvoiceBillingValues): { poNumber?: string } => {
  const poNumber = values.poNumber.trim();
  return poNumber ? { poNumber } : {};
};

/** The billing details as the API takes them: trimmed, with an optional field left empty sent as null. */
export function toInvoiceBillingRequest(values: InvoiceBillingValues) {
  return {
    billingName: values.billingName.trim(),
    billingEmail: values.billingEmail.trim(),
    billingAddress: values.billingAddress.trim(),
    poNumber: values.poNumber.trim() || null,
    vatNumber: values.vatNumber.trim() || null,
  };
}

/** Where a seat order stands, as purchasers and admins read it (LMS-151). A card order is paid once it exists. */
export const SEAT_ORDER_STATUS_LABELS: Record<string, string> = {
  Completed: "Paid",
  AwaitingInvoice: "Awaiting invoice",
  Invoiced: "Invoiced",
  Paid: "Paid",
  Cancelled: "Cancelled",
};

export const seatOrderStatusLabel = (status: string | null | undefined) =>
  (status && SEAT_ORDER_STATUS_LABELS[status]) || "";

/** The address of one of the purchaser's seat orders in My purchases. */
export const seatOrderPath = (seatOrderId: string) => `/my-purchases/seat-orders/${seatOrderId}`;

/** Shown when an invoice order can't be placed for a reason the API doesn't explain (LMS-151). */
export const INVOICE_ORDER_FAILED_MESSAGE = "We couldn't place your order. Please try again.";

/** Shown when a discount code can't be checked for a reason the API doesn't explain (LMS-187). */
export const DISCOUNT_CODE_FAILED_MESSAGE =
  "That discount code could not be applied. Check it and try again.";

/**
 * The order button: to Stripe for a card order, or placing the order straight away when it is paid by invoice
 * (LMS-151) or a discount code pays for it (LMS-187).
 */
export function continueLabel(placesOrder: boolean, isPending: boolean, total: string) {
  if (placesOrder) {
    if (isPending) return "Placing order...";
    return total ? `Place order · ${total}` : "Place order";
  }
  if (isPending) return "Redirecting to checkout...";
  return total ? `Continue to payment · ${total}` : "Continue to payment";
}

/**
 * The statuses in which an order's seats can be invited and taken up (LMS-150): a card or discount-code order once it
 * exists, and an invoice order once an admin has marked it invoiced, and still once paid (LMS-195). Mirrors
 * `SeatOrder.RedeemableStatuses` on the API, which has the final say.
 */
export const SEAT_ORDER_REDEEMABLE_STATUSES: ReadonlySet<string> = new Set([
  "Completed",
  "Invoiced",
  "Paid",
]);

/** What an invoice order awaiting its invoice says instead of offering its seats (LMS-195). */
export const AWAITING_INVOICE_SEATS_MESSAGE =
  "Seats can be shared once the invoice has been raised. You'll be able to invite people and take a seat yourself then.";

/**
 * Whether the purchaser can be offered a seat on their own order (LMS-189): an order whose seats can be taken up
 * (LMS-150; an invoice order once invoiced, LMS-195), and for an instructor-led session one that hasn't started or been
 * cancelled (its seats lapse then, LMS-190). Confirm enrolment explains anything else that stops it, such as every seat
 * being invited.
 */
export const canOfferOwnSeat = ({
  status,
  session,
}: Pick<SeatOrderResponse, "status" | "session">) =>
  SEAT_ORDER_REDEEMABLE_STATUSES.has(status ?? "") && !session?.hasStarted && !session?.isCancelled;
